<script>
    import {PATH_PREFIX} from '../../apiUrls.js';

    import DataTable from '../DataGrid/DataTable.svelte';
    import ConfirmationModal from '../ConfirmationModal.svelte';
    import TextColumnReadOnlyable from '../DataGrid/ColumnRenderers/TextColumnReadOnlyable.svelte';
    import {submit} from '../../utils.js';
    import {validateForm} from '../Form/utils.js';
    import {sgettext} from '../../i18n.js';
    import yup from 'yup';
    import Errors from '../Errors.svelte';
    import {URL_VALIDATION_REGEX} from '../../constants.js';

    export let rows = [];
    export let elementVersionId = 0;
    export let eevId = 0;
    export let activityVersionId = 0;


    let actionUrl = `${PATH_PREFIX}/sw/v2/api/elements_database/referencedocument/`;
    let referenceDocumentFind = `${PATH_PREFIX}/sw/api/referencedocument/search`;

    let selectedItem = null;
    let deleting = false;
    let deleteUrl = `${PATH_PREFIX}/sw/v2/api/elements_database/referencedocument/{0}/expire_refdoc/`
    let deleteConfirmationId = 'deleteReferenceConfirmation';
    let saving = false;
    let errors = null;
    let bgColor = '#EDF1F2';
    let showCreateNew = true;

    let schema = yup.object().shape({
        document_name: yup.string().required(sgettext("$$PLEASE_ENTER_DOCUMENT_NAME$$")).nullable(),
        document_reference: yup.string().required(sgettext("$$PLEASE_ENTER_DOCUMENT_NUMBER$$")).nullable(),
        document_url: yup.string().notRequired().nullable().matches(URL_VALIDATION_REGEX, sgettext("$$LINK_MUST_VALID_URL$$")),
        document_description: yup.string().notRequired().nullable()
    });
    let nameSuggestions = [];
    let numberSuggestions = [];
  

    $: data = rows.map(r => ({...r, fieldValidations: initialFieldValidations()}));
    $: showCreateNew = rows.find(r => !r.id) ? false : true;

    $: columns = {
        document_name: {
            label: sgettext('$$DOCUMENT_NAME_1$$'),
            renderer: TextColumnReadOnlyable,
            width: '22%',
            props: {
                onChange: makeNameSuggestions,
                searchable: true,
                conf_read_only: true,
                nameSuggestions: nameSuggestions,
                onSave: saveItem
            }
        },
        document_reference: {
            label: sgettext('$$DOCUMENT_REFERENCE_1$$'),
            renderer: TextColumnReadOnlyable,
            width: '22%',
            props: {
                onChange: makeNumberSuggestions,
                searchable: true,
                conf_read_only: true,
                numberSuggestions: numberSuggestions,
                onSave: saveItem
            }
        },
        document_description: {
            label: sgettext('$$DOCUMENT_DESCRIPTION$$'),
            renderer: TextColumnReadOnlyable,
            width: '22%',
            props: {
                searchable: false,
                conf_read_only: true
            }
        },
        document_url: {
            label: sgettext('$$DOCUMENT_URL$$'),
            renderer: TextColumnReadOnlyable,
            width: '45%',
            props: {
                searchable: true,
                conf_read_only: true
            }
        }
    }

    async function searchSuggestions(name, reference, text) {
        let url = `${referenceDocumentFind}?name=${name}&reference=${reference}`;
        if (name != '' | reference != '') {
            const response = await submit(url, 'GET');
            if (response.ok) {
                let retrieved = await response.json();
                let filtered = [];
                for (const i in retrieved) {
                    if (!(data.some((s) => s.id == retrieved[i].id))) {
                        filtered.push(retrieved[i]);
                    }
                }
                if(text === 'name'){
                    let nameStorageArr = []
                    filtered.forEach(docName => {
                        if (docName.document_name.toLowerCase().includes(name.toLowerCase())) {
                            nameStorageArr = [...nameStorageArr, makeMatchBold(docName, docName.document_name, name, text)];
                        }
                    });
                    nameSuggestions = nameStorageArr;
                }else if(text === 'number'){
                    let numberStorageArr = [];
                    filtered.forEach(docNumber => {
                        if (docNumber.document_reference.toLowerCase().includes(reference.toLowerCase())) {
                            numberStorageArr = [...numberStorageArr, makeMatchBold(docNumber, docNumber.document_reference, reference, text)];
                        }
                    });
                    numberSuggestions = numberStorageArr;
                }
            }
        } else {
            if(name == '') {
                nameSuggestions = [];
            } else if(number == '') {
                numberSuggestions = [];
            }
        }
    }

    async function makeNameSuggestions(event, row) {
        let name = row.document_name;
        await searchSuggestions(name, '', 'name');
    }

    async function makeNumberSuggestions(event, row) {
        let reference = row.document_reference;
        await searchSuggestions('', reference, 'number');
    }

    const makeMatchBold = (reference, field, value, type) => {
        let str = field.toLowerCase().indexOf(value);
        let matched = field.substring(str, str + value.length);
        let makeBold = `<strong>${matched}</strong>`;
        let boldedMatchName = '';
        let boldedMatchNumber = '';
        if(type === 'name') {
            boldedMatchName = field.replace(matched, makeBold);
            boldedMatchNumber = reference.document_reference
        }else if(type === 'number') {
            boldedMatchName = reference.document_name;
            boldedMatchNumber =  field.replace(matched, makeBold);
        }
        reference = {
            "document_reference": boldedMatchNumber,
            "document_url": reference.document_url,
            "document_description": reference.document_description,
            "document_name": boldedMatchName,
            "id": reference.id
        }
        return reference;
    }

  
    let rowActions = {
        remove: {
            iconClass: 'fas fa5-trash-alt',
            handler: (event, item) => {
                selectedItem = item;
                toggleModal('show');
            }
        }
    }

    let newRowActions = {
        add: {
            key: 'save',
            label: sgettext('$$ADD_1$$'),
            width: '100px',
            btnClass: 'outline-primary',
            handler: async (event, item) => {
                await saveItem(item);
            }
        },
        cancel: {
            label: sgettext('$$CANCEL$$'),
            width: '100px',
            btnClass: 'outline-secondary',
            handler: (event, item) => {
                selectedItem = item;
                showCreateNew = true;
                errors = null;
                nameSuggestions = [];
                numberSuggestions = [];
                handleRemove();
            }
        }
    }

    function handleCreateNew() {
        data = [...data, createNewItem()];
        showCreateNew = false;
        nameSuggestions = [];
        numberSuggestions = [];
    }

    function createNewItem() {
        return {
            document_reference: null,
            document_name: null,
            document_url: null,
            document_description: null,
            element_version: elementVersionId,
            activity_version_id: activityVersionId,
            fieldValidations: initialFieldValidations(),
            rowActions: newRowActions,
            new_row: true,
            id: false,
            eevid: eevId
        }
    }

    async function handleRemove() {
        if (selectedItem) {
            deleting = true;
            if (selectedItem.id) {
                const url = `${deleteUrl}?ev_id=${elementVersionId}`.replace('{0}', selectedItem.id);
                const resp = await submit(url, 'GET');

                if (resp.ok) {
                    data = data.filter(r => r.id !== selectedItem.id);
                    toggleModal('hide');
                    selectedItem = null;
                    rows = [...data];
                }
            } else {
                data = data.filter(r => r.id);
                toggleModal('hide');
                rows = [...data];
            }
            deleting = false;
        }
    }

    function cancelDelete() {
        toggleModal('hide');
    }

    function toggleModal(state) {
        jQuery(`#${deleteConfirmationId}`).modal(state);
    }

    async function saveItem(payload) {
        let resp = {};
        payload.element_version = elementVersionId;

        try {
            errors = null;
            await validateForm(schema, payload);
            saving = true;
           
            const url = `${actionUrl}?ev_id=${elementVersionId}`;
            resp = await submit(url, 'POST', payload);

            if (resp.ok) {
                const new_item = await resp.json();
                let items = data.filter(t => t.id);
                new_item.fieldValidations = initialFieldValidations();
                if (payload.suggestion || !payload.id) {
                    data = [...items, new_item];
                } else {
                    data = items.map(x => (x.id === new_item.id) ? new_item : x);
                }

                if (!payload.id) {
                    handleCreateNew();
                }
                rows = [...data];
            } else {
                errors = await resp.json();
            }
        } catch (err) {
            if (err.inner) {
                err.inner.forEach((er) => {
                    payload.fieldValidations[er.path].isValid = false;
                    payload.fieldValidations[er.path].errors = er.message;
                });
                data = [...data];
            }
        } finally {
            saving = false;
            const newRow = data.find(r => !r.id);
            if (!newRow) {
                showCreateNew = true;
            }
        }

    }

    function initialFieldValidations() {
        return {
            document_reference: {
                isValid: true,
                errors: ''
            },
            document_name: {
                isValid: true,
                errors: ''
            },
            document_url: {
                isValid: true,
                errors: ''
            },
            document_description: {
                isValid: true,
                errors: ''
            }
        }
    }

    
</script>

<style>
    .wrapper {
        margin-bottom: 15px;
    }

    .actions {
        margin-top: 15px;
        display: flex;
        justify-content: flex-end;
    }

</style>

<div class="wrapper">
    <p>{sgettext("$$ENTER_DOCUMENT_NUMBER_ADD_NEWSELECT$$")}</p>
    <DataTable
            {columns}
            rows={data}
            {rowActions}
            {schema}
            {bgColor}
            {saving}
    />
    <Errors {errors}/>
    {#if showCreateNew}
        <div class="actions d-flex flex-nowrap justify-content-end">
            <button class="btn btn-outline-primary" role="button" on:click={handleCreateNew}>
                {sgettext("$$CREATE_NEW_1$$")}
            </button>
        </div>
    {/if}
</div>

<ConfirmationModal
        id={deleteConfirmationId}
        title="{sgettext('$$REMOVE_REFERENCE_DOCUMENT$$')}"
        onYes={handleRemove}
        onNo={cancelDelete}
        sending={deleting}>
    <div slot="message">
        <p>{sgettext('$$SURE_WANT_REMOVE_REFERENCE_DOCUMENT$$')}</p>

        <br/>
    </div>

</ConfirmationModal>

